/**
 * CostPage — public pricing for BOTH audiences, reachable from the landing-page "The Cost" nav
 * link. Sales-oriented: sell the value, keep the numbers honest (they mirror the live paywalls —
 * consumer $480 / 4 months in Paywall.js; partner $800+GST annual or $336+GST per client in
 * PartnerSubscribe.js). Positioning rule: consumers CAN see partner pricing here, so partner
 * rates are framed as practice/scale pricing for licensed professionals who bring their own
 * advice service — never as "the same thing cheaper".
 */
import React from 'react';
import type { LucideIcon } from 'lucide-react';
import {
  BadgeDollarSign, ArrowLeft, CheckCircle2, Sparkles, TrendingUp, FileText, Bot,
  ShieldCheck, Users, Briefcase, ArrowRight, CalendarClock, Lock,
} from 'lucide-react';

interface ValuePoint {
  icon: LucideIcon;
  title: string;
  body: string;
}

const CONSUMER_INCLUDED: string[] = [
  'Every living arrangement modelled — staying home with support, residential care, retirement villages, granny flats, downsizing',
  'Your real numbers: care fees, cashflow, Age Pension and what’s left to your estate, projected over time',
  'The Suggestion Engine — hundreds of strategy combinations searched and ranked for your situation',
  'A professional Statement of Advice you can keep and share',
  'AWise, your AI guide, on every screen',
  'Everything saved securely — leave and pick up where you stopped, as often as you like',
];

const VALUE_POINTS: ValuePoint[] = [
  {
    icon: TrendingUp,
    title: 'Decisions worth tens of thousands',
    body: 'Paying for a room with a lump sum or daily fee, keeping or selling the home, timing the move — the gap between a good and poor choice is often life-changing money. This is not a decision to guess.',
  },
  {
    icon: BadgeDollarSign,
    title: 'A fraction of bespoke advice',
    body: 'Specialist aged-care financial advice is valuable — and typically costs thousands. AgedCareWise puts the same calculations and strategy thinking in your hands for one flat, upfront price.',
  },
  {
    icon: CalendarClock,
    title: 'One payment. No surprises.',
    body: 'One payment covers four full months — enough time to explore, decide, and act. No subscription, no auto-renewal, no card kept on file. If you come back later, your information is waiting.',
  },
];

const PARTNER_POINTS_ANNUAL: string[] = [
  'Up to 20 active clients included',
  'Then just $70 + GST per additional client',
  'The full client experience, under your guidance',
];

const PARTNER_POINTS_PAYG: string[] = [
  'Pay only for the clients you take on',
  'No annual commitment',
  'Switch to the annual plan any time',
];

export interface CostPageProps {
  onBack: () => void;
  onGetStarted?: () => void;
  onBecomePartner?: () => void;
}

const CostPage = ({ onBack, onGetStarted, onBecomePartner }: CostPageProps) => (
  <div className="min-h-screen bg-slate-50">
    {/* Top bar */}
    <div className="sticky top-0 z-10 border-b border-gray-200 bg-white/90 backdrop-blur">
      <div className="mx-auto flex max-w-5xl items-center justify-between px-5 py-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex items-center gap-1.5 rounded-lg px-2 py-1.5 text-sm font-medium text-gray-600 transition hover:bg-gray-100 hover:text-gray-900"
        >
          <ArrowLeft size={16} /> Back
        </button>
        <div className="text-lg font-bold">
          <span className="text-slate-900">AgedCare</span><span className="text-[#17b8e0]">Wise</span>
        </div>
      </div>
    </div>

    {/* Hero */}
    <div className="relative overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] text-white">
      <div className="pointer-events-none absolute -right-16 -top-20 h-64 w-64 rounded-full bg-white/10 blur-3xl" />
      <div className="pointer-events-none absolute -left-10 bottom-0 h-48 w-48 rounded-full bg-white/5 blur-2xl" />
      <div className="relative mx-auto max-w-5xl px-5 py-14 text-center">
        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-white/15 ring-1 ring-white/25 backdrop-blur">
          <BadgeDollarSign className="h-8 w-8" />
        </div>
        <h1 className="text-3xl font-bold sm:text-4xl">Clear, honest pricing</h1>
        <p className="mx-auto mt-3 max-w-2xl text-base text-white/90 sm:text-lg">
          One upfront price. Everything included. Whether you’re planning for your own family or
          you’re a professional guiding many — here’s exactly what it costs.
        </p>
      </div>
    </div>

    <div className="mx-auto max-w-5xl px-5 py-12 space-y-14">
      {/* Why it's worth it */}
      <div className="grid gap-5 sm:grid-cols-3">
        {VALUE_POINTS.map(({ icon: Icon, title, body }) => (
          <div key={title} className="rounded-2xl border border-gray-200 bg-white p-6 shadow-sm transition hover:shadow-md">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
              <Icon size={22} />
            </div>
            <h3 className="text-base font-bold text-slate-900">{title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-gray-600">{body}</p>
          </div>
        ))}
      </div>

      {/* Consumer price card */}
      <section aria-labelledby="cost-families">
        <div className="mb-6 text-center">
          <h2 id="cost-families" className="text-2xl font-bold text-slate-900">For you and your family</h2>
          <p className="mt-1.5 text-gray-600">Complete planning power, for one flat price.</p>
        </div>
        <div className="mx-auto max-w-3xl overflow-hidden rounded-3xl border border-blue-100 bg-white shadow-xl shadow-blue-100/60">
          <div className="relative overflow-hidden bg-gradient-to-br from-[#1a6fd4] to-[#17b8e0] px-7 py-8 text-center text-white">
            <div className="pointer-events-none absolute -right-10 -top-14 h-44 w-44 rounded-full bg-white/10 blur-2xl" />
            <div className="relative">
              <div className="text-sm font-semibold uppercase tracking-wider text-white/80">Everything included</div>
              <div className="mt-2 flex items-end justify-center gap-2">
                <span className="text-5xl font-bold">$480</span>
                <span className="pb-1.5 text-white/90">for 4 months of full access</span>
              </div>
              <div className="mt-2 text-sm text-white/85">One payment · No subscription · No auto-renewal</div>
            </div>
          </div>
          <div className="p-7 sm:p-9">
            <ul className="space-y-3">
              {CONSUMER_INCLUDED.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700 sm:text-base">
                  <CheckCircle2 size={18} className="mt-0.5 shrink-0 text-emerald-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-col items-center gap-3">
              {onGetStarted && (
                <button
                  type="button"
                  onClick={onGetStarted}
                  className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-[#1a6fd4] to-[#17b8e0] px-8 py-3 text-base font-semibold text-white shadow-lg transition hover:shadow-xl"
                >
                  <Sparkles size={18} /> Get Started
                </button>
              )}
              <div className="flex items-center gap-2 text-xs text-gray-500">
                <Lock size={13} />
                Secure payment by Stripe · Your card details never touch our servers
              </div>
            </div>
          </div>
        </div>
        <p className="mx-auto mt-4 max-w-2xl text-center text-sm text-gray-500">
          After 4 months your account locks but nothing is deleted — your plan is kept safe, and one
          payment reopens everything exactly where you left it.
        </p>
      </section>

      {/* Partner pricing */}
      <section aria-labelledby="cost-partners">
        <div className="mb-6 text-center">
          <h2 id="cost-partners" className="text-2xl font-bold text-slate-900">For professionals &amp; providers</h2>
          <p className="mx-auto mt-1.5 max-w-2xl text-gray-600">
            Accountants, financial advisers, aged-care and home-care providers, and retirement-living
            operators use AgedCareWise to guide many families — with their own expertise and service
            wrapped around it. Practice pricing reflects that scale.
          </p>
        </div>
        <div className="mx-auto grid max-w-4xl gap-5 sm:grid-cols-2">
          {/* Annual */}
          <div className="relative overflow-hidden rounded-3xl border-2 border-[#1a6fd4]/30 bg-white p-7 shadow-lg">
            <div className="absolute right-4 top-4 rounded-full bg-blue-50 px-3 py-1 text-xs font-semibold text-[#1a6fd4]">
              Best value
            </div>
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
              <Briefcase size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Annual practice plan</h3>
            <div className="mt-2 flex items-end gap-1.5">
              <span className="text-3xl font-bold text-slate-900">$800</span>
              <span className="pb-1 text-sm text-gray-500">+ GST per year</span>
            </div>
            <ul className="mt-4 space-y-2.5">
              {PARTNER_POINTS_ANNUAL.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
          {/* PAYG */}
          <div className="rounded-3xl border border-gray-200 bg-white p-7 shadow-sm">
            <div className="mb-4 flex h-11 w-11 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
              <Users size={22} />
            </div>
            <h3 className="text-lg font-bold text-slate-900">Pay per client</h3>
            <div className="mt-2 flex items-end gap-1.5">
              <span className="text-3xl font-bold text-slate-900">$336</span>
              <span className="pb-1 text-sm text-gray-500">+ GST per client</span>
            </div>
            <ul className="mt-4 space-y-2.5">
              {PARTNER_POINTS_PAYG.map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <CheckCircle2 size={16} className="mt-0.5 shrink-0 text-emerald-500" />
                  <span>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
        <div className="mt-6 text-center">
          {onBecomePartner && (
            <button
              type="button"
              onClick={onBecomePartner}
              className="inline-flex items-center gap-2 rounded-full border-2 border-[#1a6fd4] px-7 py-2.5 text-sm font-semibold text-[#1a6fd4] transition hover:bg-blue-50"
            >
              Become a partner <ArrowRight size={16} />
            </button>
          )}
          <p className="mx-auto mt-3 max-w-xl text-xs text-gray-500">
            Partner plans licence the software to your firm. You provide your advice service to your
            clients under your own arrangements — your clients never pay AgedCareWise.
          </p>
        </div>
      </section>

      {/* What's behind the price */}
      <div className="rounded-2xl border border-gray-200 bg-white p-7 shadow-sm sm:p-9">
        <div className="flex items-start gap-4">
          <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-blue-50 text-[#1a6fd4]">
            <FileText size={24} />
          </div>
          <div>
            <h2 className="text-xl font-bold text-slate-900">What’s behind the price</h2>
            <p className="mt-2 text-sm leading-relaxed text-gray-600 sm:text-base">
              AgedCareWise was built by advisers on the real, current Australian rules — means testing,
              Age Pension, care fees, the lot — and it’s kept up to date as the rules change. Your price
              includes the modelling engine, the strategy search, your Statement of Advice, the AWise
              assistant, and bank-grade protection of everything you enter.
            </p>
          </div>
        </div>
      </div>

      {/* Reassurance footer */}
      <div className="rounded-2xl bg-gradient-to-br from-slate-900 to-slate-700 p-7 text-center text-white sm:p-9">
        <div className="mx-auto mb-3 flex h-11 w-11 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20">
          <Bot size={20} />
        </div>
        <h2 className="text-lg font-bold sm:text-xl">Not sure yet?</h2>
        <p className="mx-auto mt-2 max-w-xl text-sm leading-relaxed text-white/80">
          Explore the free education topics and ask AWise your questions right from the home page —
          then step inside when you’re ready.
        </p>
        <div className="mx-auto mt-3 flex items-center justify-center gap-2 text-xs text-white/60">
          <ShieldCheck size={14} /> Prices shown for individuals include GST. Partner prices exclude GST.
        </div>
      </div>
    </div>
  </div>
);

export default CostPage;
